import devLogger from 'react-utils/devLogger';
import { isValidElement } from 'react';

// Track which functions or components we've already checked
const checkedForMemoization = new WeakSet<any>();
const MEMO_SYMBOL = Symbol.for('react.memo');

/**
 * Checks if a component or element is memoized.
 * @param componentOrElement - The component or element to check if it is memoized.
 */
export const isComponentOrElementMemoized = ({
  componentOrElement,
}: {
  componentOrElement: any;
}): boolean => {
  // null or undefined values don't need memoization checks
  if (!componentOrElement) {
    return true;
  }
  if (isValidElement(componentOrElement)) {
    const elementType = componentOrElement.type as any;
    return Boolean(
      elementType &&
        elementType.$$typeof &&
        elementType.$$typeof === MEMO_SYMBOL
    );
  }
  if (componentOrElement.$$typeof) {
    return componentOrElement.$$typeof === MEMO_SYMBOL;
  }
  return false;
};

/**
 * Logs a memoization warning if the component or element is not memoized.
 * @param componentOrElement - The component or element to check if it is memoized.
 * @param objectToMarkAsCheckedForMemoization - The object to mark as checked for memoization. Useful if checking a renderer function's result.
 * @param errorMessage - The error message to display if the component or element is not memoized.
 * @param errorKey - The error key to display if the component or element is not memoized.
 */
export const logComponentOrElementMemoizationWarning = ({
  componentOrElement,
  objectToMarkAsCheckedForMemoization,
  errorMessage,
  errorKey,
}: {
  componentOrElement: any;
  objectToMarkAsCheckedForMemoization?: any;
  errorMessage: string;
  errorKey: string;
}) => {
  if (process.env.NODE_ENV !== 'production') {
    const objectToCheck =
      objectToMarkAsCheckedForMemoization || componentOrElement;

    // WeakSet can only hold objects, not primitives
    if (
      !objectToCheck ||
      (typeof objectToCheck !== 'object' && typeof objectToCheck !== 'function')
    ) {
      return;
    }
    if (
      !checkedForMemoization.has(objectToCheck) &&
      !isComponentOrElementMemoized({
        componentOrElement,
      })
    ) {
      devLogger.warn({
        message: errorMessage,
        key: errorKey || 'foundations component not memoized',
      });
      checkedForMemoization.add(objectToCheck);
    }
  }
};
